import React, { useCallback, useContext, useMemo, useState } from 'react';
import { styled } from '@mui/material';
import {
    ColDef,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
} from 'ag-grid-community';
import { OrgChartHrContext } from 'routes/HumanResourcesModule/components/contexts/HumanResourcesContext';
import { convertOrgChartToFlatArray } from 'routes/HumanResourcesModule/Settings/OrgChartHrMappings/helpers/dataHelpers';
import MappingChoicesCellRenderer from 'routes/HumanResourcesModule/Settings/OrgChartHrMappings/components/MappingChoicesCellRenderer';
import {
    useGetAllOrgChartHrMappings,
    usePostOrgChartHrMapping,
} from 'routes/HumanResourcesModule/Settings/OrgChartHrMappings/helpers/hooks/orgChartHrMappings';
import HRMappingsHeader from 'routes/HumanResourcesModule/Settings/OrgChartHrMappings/components/HRMappingsHeader';
import LoadingGrid from 'routes/HumanResourcesModule/components/LoadingGrid';
import { OrgChartHrMappingsContext } from 'routes/HumanResourcesModule/Settings/OrgChartHrMappings/components/OrgChartHrMappingContext';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import { StyGridContainer } from 'routes/HumanResourcesModule/components/ui/StyGridContainer';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import { TreeDataForMappingGrid } from './helpers/type';
import HrCard from 'routes/HumanResourcesModule/components/HrCard';
import FullScreenProvider from 'contexts/FullScreenContextProvider';
import ConditionnalZoomIconButton from 'components/ConditionnalZoomIconButton';
import { TRACKABLE_EVENTS_METADATA } from 'helpers/trackerHelpers';
import { HUMAN_RESOURCES } from 'business/constants';
import { useDeepMemo } from 'hooks/helpers';

const StyAgGrid = styled(AgGridReact)`
    && .ag-row-group-leaf-indent {
        margin-left: 32px;
    }
    height: '100%',
    width: '100%'
`;
const floatingFilter = {
    filter: 'agTextColumnFilter',
    floatingFilterComponent: 'textFloatingFilter',
    floatingFilter: true,
    flex: 1,
    suppressFloatingFilterButton: true,
    menuTabs: [
        'filterMenuTab' as const,
        'generalMenuTab' as const,
        'columnsMenuTab' as const,
    ],
    cellStyle: {
        display: 'flex',
        alignItems: 'center',
    },
};

const autoGroupColumnDef: ColDef = {
    cellRendererParams: {
        suppressCount: true,
        checkbox: true,
    },
    ...floatingFilter,
    flex: 1,
    headerName: 'Organigramme des services RH',
    valueFormatter: (params) => params.data.label,
    filterValueGetter: (params) => params.data.label,
};

export default function HrMappings(): React.ReactElement {
    const { t } = useTranslation();
    const [isFullScreen, setIsFullScreen] = useState(false);
    const allOrgChartHrs = useContext(OrgChartHrContext);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [selectedOrgChartHRSoftwareIds, setSelectedOrgChartHRSoftwareIds] =
        useState<string[]>([]);

    const {
        data: orgChartHrMappingsData,
        isLoading: isOrgChartHrMappingsLoading,
    } = useGetAllOrgChartHrMappings();
    const orgChartHrMappings = orgChartHrMappingsData || [];

    const flattenedOrgChartHrs = useDeepMemo(
        () => convertOrgChartToFlatArray(allOrgChartHrs, orgChartHrMappings),
        [allOrgChartHrs, orgChartHrMappings],
    );

    const updateMappingMutation = usePostOrgChartHrMapping();

    const getDataPath = useCallback(
        (data: TreeDataForMappingGrid) => data.path,
        [],
    );

    const onGridReady = (event: GridReadyEvent) => {
        setGridApi(event.api);
    };

    const columnDefs: ColDef[] = useMemo(
        () => [
            {
                headerName: t('humanResources.mantyEquivalent'),
                field: 'mapping',
                cellRenderer: 'mappingChoicesCellRenderer',
                flex: 1,
                cellRendererParams: {
                    updateMappingMutation,
                },
            },
        ],
        [updateMappingMutation],
    );

    const onFilterChanged = useCallback(() => {
        if (gridApi) {
            const filterModel = gridApi.getFilterModel();
            const hasActiveFilter = Object.keys(filterModel).length > 0;
            if (!hasActiveFilter) {
                gridApi.collapseAll();
            } else {
                gridApi.forEachNode((node) => {
                    if (node.childrenAfterFilter?.length) {
                        const anyChildVisible =
                            node.childrenAfterFilter.length > 0;
                        node.setExpanded(anyChildVisible);
                    }
                });
            }
        }
    }, [gridApi]);

    const onSelectionChanged = () => {
        if (gridApi) {
            const selectedRows = gridApi
                .getSelectedRows()
                .map((row) => row.value);
            setSelectedOrgChartHRSoftwareIds(selectedRows);
        }
    };

    const getRowId = useCallback(
        (params: GetRowIdParams) => params.data.path.join('-'),
        [],
    );

    if (isOrgChartHrMappingsLoading) {
        return <LoadingGrid />;
    }

    return (
        <OrgChartHrMappingsContext.Provider value={orgChartHrMappings}>
            <FullScreenProvider
                module={HUMAN_RESOURCES}
                trackEventMetadata={{
                    gridName:
                        TRACKABLE_EVENTS_METADATA.human_resources.fullScreen
                            .mappingsOrgChartHrGrid,
                }}
            >
                <HrCard
                    title={t('humanResources.settings.mappingsOrgChartHr')}
                    startAdornment={<ConditionnalZoomIconButton />}
                    setIsFullScreen={setIsFullScreen}
                >
                    <StyGridContainer>
                        {gridApi && (
                            <HRMappingsHeader
                                selectedOrgChartHRSoftwareIds={
                                    selectedOrgChartHRSoftwareIds
                                }
                                gridApi={gridApi}
                            />
                        )}
                        <StyAgGrid
                            className="ag-theme-material"
                            domLayout={isFullScreen ? 'normal' : 'autoHeight'}
                            treeData
                            animateRows
                            getDataPath={getDataPath}
                            rowData={flattenedOrgChartHrs}
                            autoGroupColumnDef={autoGroupColumnDef}
                            rowSelection="multiple"
                            suppressRowClickSelection
                            onGridReady={onGridReady}
                            columnDefs={columnDefs}
                            rowHeight={32}
                            headerHeight={36}
                            getRowId={getRowId}
                            onSelectionChanged={onSelectionChanged}
                            components={{
                                mappingChoicesCellRenderer:
                                    MappingChoicesCellRenderer,
                                textFloatingFilter: TextFloatingFilter,
                            }}
                            columnMenu="legacy"
                            onFilterChanged={onFilterChanged}
                        />
                    </StyGridContainer>
                </HrCard>
            </FullScreenProvider>
        </OrgChartHrMappingsContext.Provider>
    );
}
